import { z } from 'zod';
import type { TpDashboardGlobalChartConfig } from '../../features/visual-analytics/dashboard/interfaces';
import type { TpChartConfigParams } from '../../features/visual-analytics/charts/chartDataBindings';
import { categoricalOrderValues } from '../../features/visual-analytics/helpers/categoricalSorters';
import { DASHBOARD_TEMPLATE_ELEMENT_TYPES as ELEMENT_TYPES } from './constants';

export const DASHBOARD_DEF_SCHEMA_VERSION = 1;

export const parentAlignmentSchema = z.enum(['horizontal', 'vertical', 'none']);
export type ParentAlignment = z.infer<typeof parentAlignmentSchema>;

export const layoutPropertiesSchema = z
  .object({
    fraction: z.number().optional(),
    height: z.number().optional(),
  })
  .optional();

export type TpLayoutProperties = z.infer<typeof layoutPropertiesSchema>;

export const widgetDataBindingSchema = z
  .object({
    dataTableId: z.string(),
    aspectBindings: z.record(z.string(), z.any()).optional(),
    compoundAspectBindings: z.record(z.string(), z.array(z.string())).optional(),
    secondAspectBindings: z.record(z.string(), z.any()).optional(),
    secondDataTableId: z.string().optional(),
  })
  .loose(); // Allows custom data binding properties

export const filterStepSchema = z
  .object({
    type: z.string(),
    dataTableId: z.string().optional(),
    colId: z.string().optional(),
    selectedCategoryIdList: z.array(z.string()).optional(),
    excludedCategoryIdList: z.array(z.string()).optional(),
  })
  .loose(); // Allows custom filter step properties

export const filterSchema = z.object({
  steps: z.array(filterStepSchema),
});

export type TpFilterSchemaDTO = z.infer<typeof filterSchema>;

export const widgetDTOSchema = z.object({
  chartType: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  selector: z.string().optional(), // Optional template selector ID (may be shared across widget instances)
  dataBinding: widgetDataBindingSchema,
  config: z.record(z.string(), z.any()).optional() as z.ZodType<TpChartConfigParams | undefined>,
  filter: filterSchema.optional(),
  layoutProperties: layoutPropertiesSchema,
});

const inlineWidgetElementSchema = widgetDTOSchema.extend({
  type: z.literal(ELEMENT_TYPES.Widget),
});

export type TpInlineWidgetElementDTO = z.infer<typeof inlineWidgetElementSchema>;

const textElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.Text),
  content: z.string(),
  layoutProperties: layoutPropertiesSchema.optional(),
});

const bitmapElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.Bitmap),
  bitmapId: z.string(),
  description: z.string().optional(),
  layoutProperties: layoutPropertiesSchema.optional(),
});

const actionButtonsElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.ActionButtons),
  actions: z.array(z.string()),
  layoutProperties: layoutPropertiesSchema.optional(),
});

// Explicit Recursive TypeScript Type to avoid z.infer Circular Reference Issues
export type TpLeafElementDTO =
  | string // Catalog reference key
  | z.infer<typeof inlineWidgetElementSchema>
  | z.infer<typeof textElementSchema>
  | z.infer<typeof bitmapElementSchema>
  | z.infer<typeof actionButtonsElementSchema>;

export type TpContainerElementDTO =
  | {
      type: typeof ELEMENT_TYPES.PairwizeComparisonGallery;
      maxCount?: number;
      dataTableId: string;
      propertyGroupId: string;
      variable1: string;
      variable2: string;
      element: TpContainerElementDTO | TpLeafElementDTO;
    }
  | {
      type: typeof ELEMENT_TYPES.Gallery;
      columnCount: number;
      maxCount?: number;
      dataTableId: string;
      propertyGroupId: string;
      variable: string;
      element: TpContainerElementDTO | TpLeafElementDTO;
    }
  | {
      type: typeof ELEMENT_TYPES.CategoriesGallery;
      columnCount: number;
      maxCount?: number;
      dataTableId: string;
      property: string;
      variable: string;
      sortBy?: (typeof categoricalOrderValues)[number];
      element: TpContainerElementDTO | TpLeafElementDTO;
    }
  | {
      type: typeof ELEMENT_TYPES.Layout;
      align: 'horizontal' | 'vertical' | 'sequence';
      layoutProperties?: TpLayoutProperties;
      elements: (TpContainerElementDTO | TpLeafElementDTO)[];
    }
  | {
      type: typeof ELEMENT_TYPES.LayoutHorizontal;
      layoutProperties?: TpLayoutProperties;
      elements: (TpContainerElementDTO | TpLeafElementDTO)[];
    }
  | {
      type: typeof ELEMENT_TYPES.LayoutVertical;
      layoutProperties?: TpLayoutProperties;
      elements: (TpContainerElementDTO | TpLeafElementDTO)[];
    }
  | {
      type: typeof ELEMENT_TYPES.LayoutSequence;
      layoutProperties?: TpLayoutProperties;
      elements: (TpContainerElementDTO | TpLeafElementDTO)[];
    }
  | {
      type: typeof ELEMENT_TYPES.Tabber;
      layoutProperties?: TpLayoutProperties;
      titles: string[];
      elements: (TpContainerElementDTO | TpLeafElementDTO)[];
    };

export type TpSectionElementDTO = TpLeafElementDTO | TpContainerElementDTO;

const lazySectionElement: z.ZodType<TpSectionElementDTO> = z.lazy(() => sectionElementSchema);

const pairwizeComparisonGalleryElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.PairwizeComparisonGallery),
  maxCount: z.number().int().optional(),
  dataTableId: z.string(),
  propertyGroupId: z.string(),
  variable1: z.string(),
  variable2: z.string(),
  element: lazySectionElement,
  layoutProperties: layoutPropertiesSchema.optional(),
});

const galleryElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.Gallery),
  columnCount: z.number().int(),
  maxCount: z.number().int().optional(),
  dataTableId: z.string(),
  propertyGroupId: z.string(),
  variable: z.string(),
  element: lazySectionElement,
  layoutProperties: layoutPropertiesSchema.optional(),
});

const categoriesGalleryElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.CategoriesGallery),
  columnCount: z.number().int(),
  maxCount: z.number().int().optional(),
  dataTableId: z.string(),
  property: z.string(),
  variable: z.string(),
  sortBy: z.enum(categoricalOrderValues).optional(),
  element: lazySectionElement,
  layoutProperties: layoutPropertiesSchema.optional(),
});

export const layoutElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.Layout),
  align: z.union([z.literal('horizontal'), z.literal('vertical'), z.literal('sequence')]),
  layoutProperties: layoutPropertiesSchema.optional(),
  elements: z.array(lazySectionElement),
});

const horizontalLayoutElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.LayoutHorizontal),
  layoutProperties: layoutPropertiesSchema.optional(),
  elements: z.array(lazySectionElement),
});

const verticalLayoutElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.LayoutVertical),
  layoutProperties: layoutPropertiesSchema.optional(),
  elements: z.array(lazySectionElement),
});

const sequenceLayoutElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.LayoutSequence),
  layoutProperties: layoutPropertiesSchema.optional(),
  elements: z.array(lazySectionElement),
});

const tabberElementSchema = z.object({
  type: z.literal(ELEMENT_TYPES.Tabber),
  layoutProperties: layoutPropertiesSchema.optional(),
  titles: z.array(z.string()),
  elements: z.array(lazySectionElement),
});

// Discriminated Union for all tagged object elements (O(1) evaluation speed)
export const typedElementSchema = z.discriminatedUnion('type', [
  inlineWidgetElementSchema,
  textElementSchema,
  bitmapElementSchema,
  actionButtonsElementSchema,
  pairwizeComparisonGalleryElementSchema,
  galleryElementSchema,
  categoriesGalleryElementSchema,
  layoutElementSchema,
  horizontalLayoutElementSchema,
  verticalLayoutElementSchema,
  sequenceLayoutElementSchema,
  tabberElementSchema,
]);

// Master union combining string catalog references and discriminated object elements
export const sectionElementSchema: z.ZodType<TpSectionElementDTO> = z.union([
  z.string(), // Widget catalog reference key
  typedElementSchema, // Tagged object elements evaluated via O(1) discriminator lookup
]);

export const sectionDTOSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  collapsed: z.boolean().default(false),
  requiredTableId: z.string().optional(),
  elements: z.array(sectionElementSchema),
});

export const topbarElementDTOSchema = z.object({ id: z.string() });

export const dashboardTemplateContentSchema = z.object({
  // Note: all fields except elements are not actually needed for rendering the dashboard.
  // They are mainly used for metadata and identification purposes: in case you want
  // to make a full YAML representation of the dashboard template.
  displayName: z.string().optional(),
  assetType: z.string().optional(),
  templateId: z.string().optional(),
  version: z.string().optional(),
  elements: z.object({
    widgets: z.record(z.string(), widgetDTOSchema),
    sections: z.array(sectionDTOSchema),
    actions: z.record(z.string(), z.any()).optional(),
    topbar: z.array(topbarElementDTOSchema).optional(),
    widgetGlobalConfig: z.record(z.string(), z.any()).optional() as z.ZodType<TpDashboardGlobalChartConfig | undefined>,
  }),
});

export const dashboardTemplateDTOSchema = z.object({
  version: z.literal(DASHBOARD_DEF_SCHEMA_VERSION),
  assetType: z.string(),
  sourceAssetId: z.string(),
  sourceAssetName: z.string(),
  baseTemplateId: z.string().optional(), // UUID of the base template this dashboard is derived from
  // These 3 fields make up the template data
  templateContent: dashboardTemplateContentSchema,
});

export type TpWidgetDTO = z.infer<typeof widgetDTOSchema>;
export type TpSectionDTO = z.infer<typeof sectionDTOSchema>;
export type TpDashboardTemplateDTO = z.infer<typeof dashboardTemplateDTOSchema>;
export type TpDashboardTemplateContentDTO = z.infer<typeof dashboardTemplateContentSchema>;
export type TpTopbarElementDTO = z.infer<typeof topbarElementDTOSchema>;
